/**
 * Menu System — dropdown menus matching the actual NEVCO Pro Video / Titler Live 5 software
 * Menu content sourced from reference screenshots in assets/screenshots/dropdowns/
 */
import { uiStore } from '../stores/uiStore';
import type { LayoutPreset } from '../stores/uiStore';
import { monitorStore } from '../stores/monitorStore';
import type { ProgramOutputType } from '../stores/monitorStore';
import { projectStore } from '../stores/projectStore';
import { selectionStore } from '../stores/selectionStore';
import { shotStore } from '../stores/shotStore';
import { openProjectSettings } from './projectSettingsDialog';
import { openFileDialog } from './fileDialog';
import {
  showConfirmDialog,
  showGenerateCacheReport,
  showAudioMonitorDialog,
  showBlackmagicAtemDialog,
  showInternetAccountsDialog,
  showZoomJoinDialog,
  showTeamsJoinDialog,
  showTeamsNdiHelp,
  showWatchFolderSetup,
  showPngSequenceSetup,
  showGettingStarted,
  showManageActivation,
  showAboutNdi,
  showApiDocumentation,
  showAnalytics,
  showCreateSupportPackage,
  showAboutCaptivate,
} from './menuDialogs';
import { navigate } from '../router';
import { templateRegistry } from '../engine/templateRegistry';
import { executeTakeItem } from '../engine/transitionEngine';
import { PATHS } from '../data/fakeFileSystem';
import type { PanelVisibility } from '@graphicsim/shared-types';
import { multiplayerStore } from '../stores/multiplayerStore';

/** Get multiplayer state (safe accessor — returns defaults when not in multiplayer) */
function getMultiplayerState() {
  return multiplayerStore.getState();
}

// Decklink live camera video (Azure Blob)
const LIVE_CAMERA_URL = 'https://switchersimvids.blob.core.windows.net/graphicsim-media/decklink1.webm';

let liveCameraCount = 0;

/** ID of the live camera project item (for multiplayer swap) */
let liveCameraItemId: string | null = null;

/** Add a live camera input — always on, auto-sends to program.
 *  In multiplayer mode, uses the current program input URL from multiplayerStore. */
export function addLiveCamera(): void {
  const mpState = getMultiplayerState();

  liveCameraCount++;
  const url = mpState.isConnected && mpState.liveCameraUrl
    ? mpState.liveCameraUrl
    : LIVE_CAMERA_URL;

  const item = projectStore.getState().addItem({
    name: `Decklink ${liveCameraCount}`,
    fieldValues: {},
    duration: 0,
    isActive: false,
    mediaType: 'video',
    mediaUrl: url,
  });
  liveCameraItemId = item.id;
  selectionStore.getState().select({ type: 'projectItem', itemId: item.id });
  monitorStore.getState().setPreview(item.id);
  // Live cameras are always on — send directly to program
  executeTakeItem(item.id);
  flashStatus(`Decklink ${liveCameraCount} added.`);
}

/**
 * Swap the live camera's video URL — called by multiplayerClient
 * when SwitcherSIM broadcasts program_changed.
 */
export function swapLiveCameraUrl(newUrl: string): void {
  if (!liveCameraItemId) return;
  const item = projectStore.getState().getItem(liveCameraItemId);
  if (!item) return;

  projectStore.getState().updateItem(liveCameraItemId, { mediaUrl: newUrl });
  // Force program re-render by re-taking the item
  executeTakeItem(liveCameraItemId);
}

export function newGraphic(): void {
  const item = projectStore.getState().addItem({
    name: 'Untitled',
    fieldValues: {},
    duration: 0,
    isActive: false,
  });
  selectionStore.getState().select({ type: 'projectItem', itemId: item.id });
  monitorStore.getState().setPreview(item.id);
  flashStatus('New graphic added to project.');
}

export function newShotLayout(): void {
  shotStore.getState().addShot(`Shot ${shotStore.getState().shots.length + 1}`, []);
  flashStatus('Blank shot added to Shot Launcher.');
}

interface MenuItem {
  label: string;
  shortcut?: string;
  action?: () => void;
  separator?: boolean;
  hasSubmenu?: boolean;
  children?: MenuItem[];
  disabled?: boolean;
  checked?: boolean;
  /** Optional icon URL shown before the label */
  icon?: string;
  /** When true, this item has coded functionality — shown in white.
   *  Unimplemented items render in muted gray. Once logic is wired up
   *  for an item, add `implemented: true` to restore white text. */
  implemented?: boolean;
}

function menuIconHtml(item: MenuItem): string {
  if (item.icon) return `<img class="menu-icon" src="${item.icon}" alt="">`;
  if (item.checked !== undefined) return `<span class="menu-check">${item.checked ? '&#x2713;' : ''}</span>`;
  return '<span class="menu-check-spacer"></span>';
}

// ── File dialog action helpers ───────────────────────────────

function openProjectFile(): void {
  openFileDialog({
    title: 'Open Project',
    startPath: PATHS.PROJECTS,
    filters: ['Project Files (*.tlp)', 'All Files (*.*)'],
    mode: 'open',
    onConfirm: (_file, fileName) => {
      if (fileName) uiStore.getState().addRecentProject(fileName);
      flashStatus(`Opened ${fileName}`);
    },
  });
}

function saveProjectAs(): void {
  openFileDialog({
    title: 'Save Project As',
    startPath: PATHS.PROJECTS,
    filters: ['Project Files (*.tlp)', 'All Files (*.*)'],
    mode: 'save',
    defaultFileName: 'project.tlp',
    onConfirm: (_file, fileName) => {
      console.debug('Save Project As:', fileName);
    },
  });
}

function exportProject(): void {
  openFileDialog({
    title: 'Export Project',
    startPath: PATHS.ROOT,
    filters: ['Project Files (*.tlp)', 'All Files (*.*)'],
    mode: 'save',
    defaultFileName: 'project.tlp',
    onConfirm: (_file, fileName) => {
      console.debug('Export Project:', fileName);
    },
  });
}

function saveDesignToLibrary(): void {
  openFileDialog({
    title: 'Save Current Design to Library',
    startPath: PATHS.TITLES,
    filters: ['SVG Files (*.svg)', 'All Files (*.*)'],
    mode: 'save',
    defaultFileName: 'design.svg',
    onConfirm: (_file, fileName) => {
      console.debug('Save Design to Library:', fileName);
    },
  });
}

export function importLayer(): void {
  openFileDialog({
    title: 'Import Layer',
    startPath: PATHS.LESSON_MEDIA,
    filters: ['All Media (*.png;*.jpg;*.svg)', 'Image Files (*.png;*.jpg)', 'SVG Files (*.svg)', 'All Files (*.*)'],
    mode: 'open',
    onConfirm: (file) => {
      if (!file) return;
      const item = projectStore.getState().addItem({
        name: file.name,
        fieldValues: {},
        duration: 0,
        isActive: false,
        mediaType: 'layer',
        mediaUrl: file.assetUrl,
      });
      selectionStore.getState().select({ type: 'projectItem', itemId: item.id });
      monitorStore.getState().setPreview(item.id);
    },
  });
}

export function importGraphic(): void {
  openFileDialog({
    title: 'Import Graphic',
    startPath: PATHS.TITLES,
    filters: ['SVG Files (*.svg)', 'All Files (*.*)'],
    mode: 'open',
    onConfirm: (file) => {
      if (!file || !file.templateId) {
        console.debug('Import Graphic: no matching template for', file?.name);
        return;
      }
      // Reuse the handleProjectDrop pattern
      const template = templateRegistry.getTemplate(file.templateId);
      if (!template) return;
      const item = projectStore.getState().addItem({
        templateId: file.templateId,
        name: template.name,
        fieldValues: templateRegistry.getDefaultFieldValues(file.templateId),
        duration: template.duration ?? 0,
        isActive: false,
      });
      selectionStore.getState().select({ type: 'projectItem', itemId: item.id });
      monitorStore.getState().setPreview(item.id);
    },
  });
}

export function importMedia(): void {
  openFileDialog({
    title: 'Select media',
    startPath: PATHS.LESSON_MEDIA,
    filters: ['All Media (*.png;*.jpg;*.mp4;*.mkv;*.mpg;*.webm)', 'Image Files (*.png;*.jpg)', 'All Files (*.*)'],
    mode: 'open',
    onConfirm: (file) => {
      if (!file) return;
      const ext = (file.extension || '').toLowerCase();
      const isVideo = ['.mp4', '.mkv', '.mpg', '.avi', '.mov', '.webm'].includes(ext);
      const item = projectStore.getState().addItem({
        name: file.name,
        fieldValues: {},
        duration: 0,
        isActive: false,
        mediaType: isVideo ? 'video' : 'image',
        mediaUrl: file.assetUrl,
      });
      selectionStore.getState().select({ type: 'projectItem', itemId: item.id });
      monitorStore.getState().setPreview(item.id);
    },
  });
}

export function importAfterEffects(): void {
  openFileDialog({
    title: 'Import After Effects',
    startPath: PATHS.ROOT,
    filters: ['After Effects (*.aep)', 'All Files (*.*)'],
    mode: 'open',
    onConfirm: (file) => {
      if (!file) return;
      const item = projectStore.getState().addItem({
        name: file.name,
        fieldValues: {},
        duration: 0,
        isActive: false,
        mediaType: 'ae',
        mediaUrl: file.assetUrl,
      });
      selectionStore.getState().select({ type: 'projectItem', itemId: item.id });
      monitorStore.getState().setPreview(item.id);
    },
  });
}

export function importShotLayout(): void {
  openFileDialog({
    title: 'Import Shot Layout',
    startPath: PATHS.ROOT,
    filters: ['Shot Layout (*.xml)', 'All Files (*.*)'],
    mode: 'open',
    onConfirm: (file) => {
      if (!file) return;
      const item = projectStore.getState().addItem({
        name: file.name,
        fieldValues: {},
        duration: 0,
        isActive: false,
        mediaType: 'shot-layout',
        mediaUrl: file.assetUrl,
      });
      selectionStore.getState().select({ type: 'projectItem', itemId: item.id });
      monitorStore.getState().setPreview(item.id);
    },
  });
}

export function importTransitions(): void {
  openFileDialog({
    title: 'Import Transitions',
    startPath: PATHS.ROOT,
    filters: ['Transition Files (*.xml;*.json)', 'All Files (*.*)'],
    mode: 'open',
    onConfirm: (file) => {
      if (!file) return;
      const item = projectStore.getState().addItem({
        name: file.name,
        fieldValues: {},
        duration: 0,
        isActive: false,
        mediaType: 'layer',
        mediaUrl: file.assetUrl,
      });
      selectionStore.getState().select({ type: 'projectItem', itemId: item.id });
      monitorStore.getState().setPreview(item.id);
    },
  });
}

function importContentPackage(): void {
  openFileDialog({
    title: 'Import Content Package',
    startPath: PATHS.ROOT,
    filters: ['Content Package (*.zip)', 'All Files (*.*)'],
    mode: 'open',
    onConfirm: (file, fileName) => {
      console.debug('Import Content Package:', fileName, file);
    },
  });
}

function saveGraphicToLibrary(): void {
  openFileDialog({
    title: 'Save Graphic to Library',
    startPath: PATHS.TITLES,
    filters: ['SVG Files (*.svg)', 'All Files (*.*)'],
    mode: 'save',
    defaultFileName: 'graphic.svg',
    onConfirm: (_file, fileName) => {
      console.debug('Save Graphic to Library:', fileName);
    },
  });
}

// ── Status flash helper ──────────────────────────────────────

export function flashStatus(text: string, ms = 2000): void {
  uiStore.getState().setStatus(text);
  setTimeout(() => {
    if (uiStore.getState().statusText === text) uiStore.getState().setStatus('');
  }, ms);
}

// ── New Project action ───────────────────────────────────────

export function newProjectAction(): void {
  showConfirmDialog({
    title: 'New Project',
    message: 'Create a new project? Unsaved changes will be lost.',
    confirmLabel: 'New Project',
    onConfirm: () => {
      projectStore.getState().resetProject();
      selectionStore.getState().clearSelection();
      monitorStore.getState().setPreview(null);
      monitorStore.getState().clearProgram();
      uiStore.getState().resetPanels();
      flashStatus('New project created.');
    },
  });
}

// ── Save Project action ──────────────────────────────────────

export function saveProjectAction(): void {
  flashStatus('Project saved.');
}

// ── Menu definitions ─────────────────────────────────────────

function getMenuDefinitions(): Record<string, MenuItem[]> {
  const ui = uiStore.getState();
  const panels = ui.panels;
  const outputType = monitorStore.getState().programOutputType;

  const setOutput = (type: ProgramOutputType) => () => monitorStore.getState().setProgramOutput(type);

  return {
    // ── File ──────────────────────────────────────────────
    'File': [
      { label: 'New Project', shortcut: 'Ctrl+N', action: newProjectAction, implemented: true },
      { label: 'Open Project...', shortcut: 'Ctrl+O', action: () => flashStatus('Open Project...') },
      { label: 'Save Project', shortcut: 'Ctrl+S', action: saveProjectAction },
      { label: 'Save Project As...', action: () => flashStatus('Save Project As...') },
      { label: 'Export Project...', action: () => flashStatus('Export Project...') },
      { label: 'Save Current Design to Library', action: () => flashStatus('Save Current Design to Library') },
      { label: '', separator: true },
      {
        label: 'Recent Projects', hasSubmenu: true,
        children: [
          { label: '(No Recent Projects)', disabled: true },
        ],
      },
      { label: '', separator: true },
      {
        label: 'New', hasSubmenu: true, implemented: true,
        children: [
          {
            label: 'Graphic', implemented: true, icon: '/assets/icons/graphic.png',
            action: () => newGraphic(),
          },
          {
            label: 'Shot Layout', implemented: true, icon: '/assets/icons/shot.png',
            action: () => newShotLayout(),
          },
          { label: 'Media Sequence', action: importMedia, implemented: true, icon: '/assets/icons/sequence.png' },
          { label: 'Transition', action: () => flashStatus('Transition'), icon: '/assets/icons/transition.png' },
          {
            label: 'Live Audio/Video', hasSubmenu: true, implemented: true,
            children: [
              {
                label: 'Windows Device', implemented: true,
                action: () => addLiveCamera(),
              },
              {
                label: 'Blackmagic Decklink', implemented: true,
                action: () => addLiveCamera(),
              },
              { label: 'Audio Device', disabled: true },
              { label: 'Screen', disabled: true },
            ],
          },
        ],
      },
      {
        label: 'Import', hasSubmenu: true, implemented: true,
        children: [
          { label: 'Layer...', action: importLayer, implemented: true },
          { label: 'Graphic...', action: importGraphic, implemented: true },
          { label: 'Media...', action: importMedia, implemented: true },
          { label: 'After Effects...', action: importAfterEffects, implemented: true },
          { label: 'Shot Layout...', action: importShotLayout, implemented: true },
          { label: 'Transitions...', action: importTransitions, implemented: true },
        ],
      },
      { label: 'Save Current Graphic to Library', action: () => flashStatus('Save Current Graphic to Library') },
      { label: '', separator: true },
      { label: 'Import Content Package...', action: () => flashStatus('Import Content Package...') },
      { label: 'Generate Cache Report...', action: () => flashStatus('Generate Cache Report...') },
      { label: '', separator: true },
      {
        label: 'Exit', shortcut: 'Ctrl+Q, Ctrl+W', implemented: true,
        action: () => showConfirmDialog({
          title: 'Exit',
          message: 'Exit the application? Unsaved changes will be lost.',
          confirmLabel: 'Exit',
          onConfirm: () => navigate('/'),
        }),
      },
    ],

    // ── View ─────────────────────────────────────────────
    'View': [
      { label: 'Toolbar', checked: ui.toolbarVisible, action: () => uiStore.getState().toggleToolbar(), implemented: true },
      { label: 'Properties', shortcut: 'F3', checked: panels.properties, action: () => uiStore.getState().togglePanel('properties'), implemented: true },
      { label: 'Graphics Library', shortcut: 'F4', checked: panels.library, action: () => uiStore.getState().togglePanel('library'), implemented: true },
      { label: 'Program Monitor - [1280 x 720, 30 fps, Progressive]', shortcut: 'F5', checked: panels.program, action: () => uiStore.getState().togglePanel('program'), implemented: true },
      { label: 'Preview', shortcut: 'F6', checked: panels.preview, action: () => uiStore.getState().togglePanel('preview'), implemented: true },
      { label: 'Video Switcher', shortcut: 'F7', checked: panels.switcher, action: () => uiStore.getState().togglePanel('switcher'), implemented: true },
      { label: 'Audio Mixer', shortcut: 'F8', checked: panels.audio, action: () => uiStore.getState().togglePanel('audio'), implemented: true },
      { label: 'Shot Launcher', shortcut: 'F9', checked: panels.shots, action: () => uiStore.getState().togglePanel('shots'), implemented: true },
      { label: 'Recall Keypad', action: () => flashStatus('Recall Keypad — no action.'), implemented: true },
      { label: '', separator: true },
      {
        label: 'Layout', hasSubmenu: true, implemented: true,
        children: [
          ...(['Compact', 'Default', 'Express', 'Live', 'Scoretable', 'Setup'] as const).map((name) => {
            const key = name.toLowerCase() as LayoutPreset;
            return {
              label: name,
              checked: ui.activeLayout === key,
              action: () => {
                uiStore.getState().setActiveLayout(key);
                flashStatus(`Layout: ${name}`);
              },
              implemented: true,
            };
          }),
          { label: '', separator: true },
          {
            label: 'Custom Layout',
            checked: ui.activeLayout === 'custom',
            action: () => {
              if (ui.customLayoutPanels) {
                uiStore.getState().setActiveLayout('custom');
                flashStatus('Custom layout applied.');
              } else {
                flashStatus('No custom layout saved.');
              }
            },
            implemented: true,
          },
          {
            label: 'Save Current to Custom',
            action: () => {
              uiStore.getState().saveCustomLayout();
              flashStatus('Current layout saved to Custom.');
            },
            implemented: true,
          },
          { label: '', separator: true },
          {
            label: 'Reset All',
            action: () => {
              uiStore.getState().setActiveLayout('live');
              flashStatus('All layouts reset.');
            },
            implemented: true,
          },
        ],
      },
      {
        label: 'Workspace Focus', hasSubmenu: true, implemented: true,
        children: [
          {
            label: 'Project List',
            checked: ui.workspaceFocus === 'project',
            action: () => {
              uiStore.getState().setWorkspaceFocus('project');
              flashStatus(uiStore.getState().workspaceFocus === 'project' ? 'Project List locked.' : 'Workspace focus cleared.');
            },
            implemented: true,
          },
          {
            label: 'Shot Launcher',
            checked: ui.workspaceFocus === 'shots',
            action: () => {
              uiStore.getState().setWorkspaceFocus('shots');
              flashStatus(uiStore.getState().workspaceFocus === 'shots' ? 'Shot Launcher locked.' : 'Workspace focus cleared.');
            },
            implemented: true,
          },
        ],
      },
      { label: '', separator: true },
      {
        label: 'Preview Background', hasSubmenu: true, implemented: true,
        children: [
          { label: 'Program Monitor', checked: ui.previewBackground === 'program-monitor', action: () => uiStore.getState().setPreviewBackground('program-monitor'), implemented: true },
          { label: 'Shot Layout', checked: ui.previewBackground === 'shot-layout', action: () => uiStore.getState().setPreviewBackground('shot-layout'), implemented: true },
          { label: '', separator: true },
          { label: 'Black', checked: ui.previewBackground === 'black', action: () => uiStore.getState().setPreviewBackground('black'), implemented: true },
          { label: 'Grey', checked: ui.previewBackground === 'grey', action: () => uiStore.getState().setPreviewBackground('grey'), implemented: true },
          { label: 'White', checked: ui.previewBackground === 'white', action: () => uiStore.getState().setPreviewBackground('white'), implemented: true },
          { label: 'Alpha', checked: ui.previewBackground === 'alpha', action: () => uiStore.getState().setPreviewBackground('alpha'), implemented: true },
          { label: '', separator: true },
          {
            label: 'Image\u2026',
            action: () => {
              openFileDialog({
                title: 'Select Background Image',
                startPath: PATHS.LESSON_MEDIA,
                filters: ['Image Files (*.png;*.jpg;*.bmp)', 'All Files (*.*)'],
                mode: 'open',
                onConfirm: (file) => {
                  if (file?.assetUrl) {
                    uiStore.getState().setPreviewBackgroundImage(file.assetUrl);
                    flashStatus('Preview background image set.');
                  }
                },
              });
            },
            implemented: true,
          },
        ],
      },
      { label: '', separator: true },
      { label: 'Message Log', action: () => flashStatus('Message Log — no action.'), implemented: true },
    ],

    // ── Settings ─────────────────────────────────────────
    'Settings': [
      { label: 'Final Render Quality', checked: ui.finalRenderQuality, action: () => uiStore.getState().toggleFinalRenderQuality(), implemented: true },
      { label: '', separator: true },
      { label: 'Use Instant Search', checked: ui.useInstantSearch, action: () => uiStore.getState().toggleInstantSearch(), implemented: true },
      { label: 'Allow Keyboard Matching for Values Grid', checked: ui.allowKeyboardMatching, action: () => uiStore.getState().toggleKeyboardMatching(), implemented: true },
      { label: 'Force AE to be Offline', checked: ui.forceAeOffline, action: () => uiStore.getState().toggleForceAeOffline(), implemented: true },
      { label: '', separator: true },
      {
        label: 'Language', hasSubmenu: true, implemented: true,
        children: (['English', 'Spanish', 'French', 'German', 'Portuguese', 'Japanese', 'Chinese'] as const).map((lang) => ({
          label: lang,
          checked: ui.language === lang,
          action: () => uiStore.getState().setLanguage(lang),
          implemented: true,
        })),
      },
      { label: '', separator: true },
      { label: 'Audio Monitor', action: showAudioMonitorDialog, implemented: true },
      { label: '', separator: true },
      { label: 'Project...', action: () => openProjectSettings(), implemented: true },
      { label: 'Blackmagic Design ATEM...', action: showBlackmagicAtemDialog, implemented: true },
      { label: '', separator: true },
      { label: 'Internet Accounts...', action: showInternetAccountsDialog, implemented: true },
    ],

    // ── Conferencing ─────────────────────────────────────
    'Conferencing': [
      { label: 'Zoom: Join Existing Meeting...', action: showZoomJoinDialog, implemented: true },
      { label: 'Zoom: Leave Meeting', disabled: true },
      { label: '', separator: true },
      { label: 'Microsoft Teams: Join Meeting...', action: showTeamsJoinDialog, implemented: true },
      { label: 'Microsoft Teams: NDI Configuration Help...', action: showTeamsNdiHelp, implemented: true },
    ],

    // ── Program Out ──────────────────────────────────────
    'Program Out': [
      { label: 'No Monitor', checked: outputType === 'none', action: setOutput('none'), implemented: true },
      { label: 'NDI', checked: outputType === 'ndi', action: setOutput('ndi'), implemented: true },
      { label: 'HDMI', checked: outputType === 'hdmi', action: setOutput('hdmi'), implemented: true },
      { label: 'Watch Folder', checked: outputType === 'watchfolder', action: setOutput('watchfolder'), implemented: true },
      { label: 'PNG Sequence', checked: outputType === 'pngsequence', action: setOutput('pngsequence'), implemented: true },
      { label: 'OBS', checked: outputType === 'obs', action: setOutput('obs'), implemented: true },
      { label: 'Virtual Webcam', checked: outputType === 'virtualwebcam', action: setOutput('virtualwebcam'), implemented: true },
      { label: '', separator: true },
      {
        label: 'HDMI Setup', hasSubmenu: true, implemented: true,
        children: [
          { label: 'Device 1 (Simulated)', checked: monitorStore.getState().hdmiDevice === 1, action: () => { monitorStore.getState().setHdmiDevice(1); flashStatus('HDMI Device 1 selected.'); }, implemented: true },
          { label: 'Device 2 (Simulated)', checked: monitorStore.getState().hdmiDevice === 2, action: () => { monitorStore.getState().setHdmiDevice(2); flashStatus('HDMI Device 2 selected.'); }, implemented: true },
        ],
      },
      { label: 'Watch Folder Setup', action: showWatchFolderSetup, implemented: true },
      { label: 'PNG Sequence Setup', action: showPngSequenceSetup, implemented: true },
    ],

    // ── Help ─────────────────────────────────────────────
    'Help': [
      { label: 'Help', shortcut: 'F1', action: showGettingStarted, implemented: true },
      { label: 'Getting Started...', action: showGettingStarted, implemented: true },
      { label: 'Shortcuts', action: () => showShortcutsHelp(), implemented: true },
      { label: '', separator: true },
      { label: 'Manage Activation', action: showManageActivation, implemented: true },
      { label: 'About NDI...', action: showAboutNdi, implemented: true },
      { label: 'API Documentation...', action: showApiDocumentation, implemented: true },
      { label: 'Analytics...', action: showAnalytics, implemented: true },
      { label: 'Create Support Package...', action: showCreateSupportPackage, implemented: true },
      { label: 'About Captivate', action: showAboutCaptivate, implemented: true },
    ],
  };
}

let activeMenu: HTMLElement | null = null;
let activeAnchor: HTMLElement | null = null;
let activeSubmenu: HTMLElement | null = null;
let activeSubSubmenu: HTMLElement | null = null;

export function initMenuSystem(): void {
  document.querySelectorAll<HTMLElement>('.menu-item').forEach((item) => {
    item.addEventListener('click', (e) => {
      e.stopPropagation();
      const label = item.textContent?.trim() || '';
      // Toggle: clicking the same menu item closes it
      if (activeMenu && activeAnchor === item) {
        closeMenu();
        return;
      }
      openMenu(label, item);
    });

    item.addEventListener('mouseenter', () => {
      if (activeMenu) {
        const label = item.textContent?.trim() || '';
        openMenu(label, item);
      }
    });
  });

  // Close menu on outside click
  document.addEventListener('click', closeMenu);
}

function openMenu(menuName: string, anchor: HTMLElement): void {
  const menus = getMenuDefinitions();
  const items = menus[menuName];
  if (!items) return;

  closeMenu();

  const dropdown = document.createElement('div');
  dropdown.className = 'menu-dropdown';
  dropdown.innerHTML = items.map((item) => {
    if (item.separator) return `<div class="menu-separator"></div>`;
    const classes = ['menu-dropdown-item'];
    if (item.disabled) classes.push('disabled');
    if (!item.implemented) classes.push('not-implemented');
    if (item.hasSubmenu && item.children) classes.push('has-submenu');
    return `<div class="${classes.join(' ')}">
      ${menuIconHtml(item)}
      <span class="menu-label">${item.label}</span>
      ${item.shortcut ? `<span class="menu-shortcut">${item.shortcut}</span>` : ''}
      ${item.hasSubmenu ? `<span class="menu-submenu-arrow">&#x25B6;</span>` : ''}
    </div>`;
  }).join('');

  const rect = anchor.getBoundingClientRect();
  dropdown.style.cssText = `position:fixed;left:${rect.left}px;top:${rect.bottom}px;z-index:1500;`;
  document.body.appendChild(dropdown);
  activeMenu = dropdown;
  activeAnchor = anchor;

  // Mark anchor as active
  anchor.classList.add('active');

  // Attach click handlers and submenu hover to items
  let itemIndex = 0;
  dropdown.querySelectorAll<HTMLElement>('.menu-dropdown-item').forEach((el) => {
    // Find the matching item (skip separators)
    while (itemIndex < items.length && items[itemIndex].separator) itemIndex++;
    if (itemIndex >= items.length) return;
    const menuItem = items[itemIndex];
    itemIndex++;

    // Submenu on hover
    if (menuItem.hasSubmenu && menuItem.children && !menuItem.disabled) {
      el.addEventListener('mouseenter', () => {
        showSubmenu(el, menuItem.children!);
      });
      el.addEventListener('mouseleave', (e) => {
        // Only close if not moving to the submenu itself
        const related = e.relatedTarget as HTMLElement;
        if (!activeSubmenu?.contains(related)) {
          closeSubmenu();
        }
      });
    } else {
      el.addEventListener('mouseenter', () => {
        closeSubmenu();
      });
    }

    if (!menuItem.disabled && menuItem.action && !(menuItem.hasSubmenu && menuItem.children)) {
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        menuItem.action!();
        closeMenu();
      });
    }
  });
}

function showSubmenu(parentEl: HTMLElement, children: MenuItem[]): void {
  closeSubmenu();
  const sub = document.createElement('div');
  sub.className = 'menu-submenu';
  sub.innerHTML = children.map((item) => {
    if (item.separator) return `<div class="menu-separator"></div>`;
    const classes = ['menu-dropdown-item'];
    if (item.disabled) classes.push('disabled');
    if (!item.implemented) classes.push('not-implemented');
    if (item.hasSubmenu && item.children) classes.push('has-submenu');
    return `<div class="${classes.join(' ')}">
      ${menuIconHtml(item)}
      <span class="menu-label">${item.label}</span>
      ${item.shortcut ? `<span class="menu-shortcut">${item.shortcut}</span>` : ''}
      ${item.hasSubmenu ? `<span class="menu-submenu-arrow">&#x25B6;</span>` : ''}
    </div>`;
  }).join('');

  const rect = parentEl.getBoundingClientRect();
  sub.style.cssText = `position:fixed;left:${rect.right}px;top:${rect.top}px;z-index:1501;`;
  document.body.appendChild(sub);
  activeSubmenu = sub;

  // Keep submenu open when hovering over it (or its sub-submenu)
  sub.addEventListener('mouseleave', (e) => {
    const related = e.relatedTarget as HTMLElement;
    if (!parentEl.contains(related) && !activeSubSubmenu?.contains(related)) {
      closeSubmenu();
    }
  });

  // Attach click handlers and nested submenu hover
  let childIndex = 0;
  sub.querySelectorAll<HTMLElement>('.menu-dropdown-item').forEach((el) => {
    while (childIndex < children.length && children[childIndex].separator) childIndex++;
    if (childIndex >= children.length) return;
    const childItem = children[childIndex];
    childIndex++;

    if (childItem.hasSubmenu && childItem.children && !childItem.disabled) {
      el.addEventListener('mouseenter', () => {
        showSubSubmenu(el, childItem.children!);
      });
      el.addEventListener('mouseleave', (e) => {
        const related = e.relatedTarget as HTMLElement;
        if (!activeSubSubmenu?.contains(related)) {
          closeSubSubmenu();
        }
      });
    } else {
      el.addEventListener('mouseenter', () => {
        closeSubSubmenu();
      });
      if (!childItem.disabled && childItem.action) {
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          childItem.action!();
          closeMenu();
        });
      }
    }
  });
}

function showSubSubmenu(parentEl: HTMLElement, children: MenuItem[]): void {
  closeSubSubmenu();
  const sub = document.createElement('div');
  sub.className = 'menu-submenu';
  sub.innerHTML = children.map((item) => {
    if (item.separator) return `<div class="menu-separator"></div>`;
    const classes = ['menu-dropdown-item'];
    if (item.disabled) classes.push('disabled');
    if (!item.implemented) classes.push('not-implemented');
    return `<div class="${classes.join(' ')}">
      ${menuIconHtml(item)}
      <span class="menu-label">${item.label}</span>
    </div>`;
  }).join('');

  const rect = parentEl.getBoundingClientRect();
  sub.style.cssText = `position:fixed;left:${rect.right}px;top:${rect.top}px;z-index:1502;`;
  document.body.appendChild(sub);
  activeSubSubmenu = sub;

  sub.addEventListener('mouseleave', (e) => {
    const related = e.relatedTarget as HTMLElement;
    if (!parentEl.contains(related) && !activeSubmenu?.contains(related)) {
      closeSubSubmenu();
    }
  });

  let childIndex = 0;
  sub.querySelectorAll<HTMLElement>('.menu-dropdown-item').forEach((el) => {
    while (childIndex < children.length && children[childIndex].separator) childIndex++;
    if (childIndex >= children.length) return;
    const childItem = children[childIndex];
    childIndex++;

    if (!childItem.disabled && childItem.action) {
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        childItem.action!();
        closeMenu();
      });
    }
  });
}

function closeSubSubmenu(): void {
  if (activeSubSubmenu) {
    activeSubSubmenu.remove();
    activeSubSubmenu = null;
  }
}

function closeSubmenu(): void {
  closeSubSubmenu();
  if (activeSubmenu) {
    activeSubmenu.remove();
    activeSubmenu = null;
  }
}

function closeMenu(): void {
  closeSubmenu();
  if (activeMenu) {
    activeMenu.remove();
    activeMenu = null;
    activeAnchor = null;
  }
  document.querySelectorAll('.menu-item.active').forEach((el) => el.classList.remove('active'));
}

function showShortcutsHelp(): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal-dialog" style="min-width:380px;">
      <div class="modal-header">
        <span class="modal-title">Keyboard Shortcuts</span>
        <button class="modal-close">&times;</button>
      </div>
      <div class="modal-body" style="font-size:11px;">
        <table style="width:100%;border-collapse:collapse;">
          <tr><td style="padding:3px 8px;color:var(--text-secondary);">F1</td><td style="color:var(--text-muted);">Help</td></tr>
          <tr><td style="padding:3px 8px;color:var(--text-secondary);">F3</td><td style="color:var(--text-muted);">Toggle Properties</td></tr>
          <tr><td style="padding:3px 8px;color:var(--text-secondary);">F4</td><td style="color:var(--text-muted);">Toggle Graphics Library</td></tr>
          <tr><td style="padding:3px 8px;color:var(--text-secondary);">F5</td><td style="color:var(--text-muted);">Toggle Program Monitor</td></tr>
          <tr><td style="padding:3px 8px;color:var(--text-secondary);">F6</td><td style="color:var(--text-muted);">Toggle Preview</td></tr>
          <tr><td style="padding:3px 8px;color:var(--text-secondary);">F7</td><td style="color:var(--text-muted);">Toggle Video Switcher</td></tr>
          <tr><td style="padding:3px 8px;color:var(--text-secondary);">F8</td><td style="color:var(--text-muted);">Toggle Audio Mixer</td></tr>
          <tr><td style="padding:3px 8px;color:var(--text-secondary);">F9</td><td style="color:var(--text-muted);">Toggle Shot Launcher</td></tr>
          <tr><td colspan="2" style="padding:6px 0;"><hr style="border:none;border-top:1px solid #333;"></td></tr>
          <tr><td style="padding:3px 8px;color:var(--text-secondary);">Space / Enter</td><td style="color:var(--text-muted);">Take to Program</td></tr>
          <tr><td style="padding:3px 8px;color:var(--text-secondary);">Delete</td><td style="color:var(--text-muted);">Delete selected items</td></tr>
          <tr><td style="padding:3px 8px;color:var(--text-secondary);">Escape</td><td style="color:var(--text-muted);">Clear selection / Close menu</td></tr>
          <tr><td style="padding:3px 8px;color:var(--text-secondary);">Ctrl+A</td><td style="color:var(--text-muted);">Select all items</td></tr>
          <tr><td style="padding:3px 8px;color:var(--text-secondary);">Ctrl+N</td><td style="color:var(--text-muted);">New Project</td></tr>
          <tr><td style="padding:3px 8px;color:var(--text-secondary);">Ctrl+O</td><td style="color:var(--text-muted);">Open Project</td></tr>
          <tr><td style="padding:3px 8px;color:var(--text-secondary);">Ctrl+S</td><td style="color:var(--text-muted);">Save Project</td></tr>
        </table>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.querySelector('.modal-close')?.addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).classList.contains('modal-overlay')) overlay.remove();
  });
}
